import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { allowedPaths, allowedPathsForUser, homePathForRole } from "./lib/constants";
import ScreenGuard from "./components/ScreenGuard";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import Dashboard from "./pages/Dashboard";
import Residents from "./pages/Residents";
import Formation from "./pages/Formation";
import Incontinence from "./pages/Incontinence";
import Agenda from "./pages/Agenda";
import SuiviCommercial from "./pages/SuiviCommercial";
import TableauCeo from "./pages/TableauCeo";
import PlanningActivite from "./pages/PlanningActivite";
import SuiviPP from "./pages/SuiviPP";
import PPDetail from "./pages/PPDetail";
import TableauPP from "./pages/TableauPP";
import Recommandations from "./pages/Recommandations";
import DomainesPP from "./pages/DomainesPP";
import ObjectifsTypesPP from "./pages/ObjectifsTypesPP";
import Guide from "./pages/Guide";
import VieSociale from "./pages/VieSociale";
import EspacePartenaires from "./pages/EspacePartenaires";
import Decouvrir from "./pages/Decouvrir";
import SatisfactionPublic from "./pages/SatisfactionPublic";
import Utilisateurs from "./pages/Utilisateurs";
import Audit from "./pages/Audit";
import CategoriesActivites from "./pages/CategoriesActivites";
import Messagerie from "./pages/Messagerie";
import CuisineAgenda from "./pages/CuisineAgenda";
import CuisinePlanning from "./pages/CuisinePlanning";
import MenuSemaine from "./pages/MenuSemaine";
import CuisineInventaire from "./pages/CuisineInventaire";
import PetitDejeuner from "./pages/PetitDejeuner";
import MenuPlats from "./pages/MenuPlats";
import RepasPreparation from "./pages/RepasPreparation";
import Habitants from "./pages/Habitants";
import StatsRepas from "./pages/StatsRepas";
import { Loader2 } from "lucide-react";

function Protected({ children, path, bare }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const redirectUnauth = () => {
    // Racine = point d'entrée public (QR familles/partenaires) : vitrine publique, jamais le formulaire de connexion.
    if (location.pathname === "/") return <Navigate to="/decouvrir" replace />;
    // Liens profonds (espace staff) : redirection vers la connexion en conservant la destination.
    const next = location.pathname + location.search;
    const qs = next && next !== "/" ? `?next=${encodeURIComponent(next)}` : "";
    return <Navigate to={`/login${qs}`} replace />;
  };
  if (loading || user === null) {
    // Visiteur sans session connue : redirection immédiate (vitrine publique pour la racine, connexion sinon).
    if (localStorage.getItem("cd_authed") !== "1") return redirectUnauth();
    return <div className="min-h-screen app-bg flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!user) return redirectUnauth();
  if (user.must_change_password) return <ChangePassword forced />;
  if (path && !allowedPathsForUser(user).includes(path)) return <Navigate to={homePathForRole(user.role)} replace />;
  if (bare) return children;
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user && !user.must_change_password) {
    const next = new URLSearchParams(location.search).get("next");
    const dest = next && allowedPathsForUser(user).includes(next.split("?")[0]) ? next : homePathForRole(user.role);
    return <Navigate to={dest} replace />;
  }
  if (user && user.must_change_password) return <ChangePassword forced />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/decouvrir" element={<Decouvrir />} />
            <Route path="/satisfaction/:token" element={<SatisfactionPublic />} />
            <Route path="/" element={<Protected path="/"><Dashboard /></Protected>} />
            <Route path="/agenda" element={<Protected path="/agenda"><Agenda /></Protected>} />
            <Route path="/residents" element={<Protected path="/residents"><Residents /></Protected>} />
            <Route path="/formation" element={<Protected path="/formation"><Formation /></Protected>} />
            <Route path="/incontinence" element={<Protected path="/incontinence"><Incontinence /></Protected>} />
            <Route path="/suivi-commercial" element={<Protected path="/suivi-commercial"><SuiviCommercial /></Protected>} />
            <Route path="/tableau-ceo" element={<Protected path="/tableau-ceo"><TableauCeo /></Protected>} />
            <Route path="/planning-activite" element={<Protected path="/planning-activite"><PlanningActivite /></Protected>} />
            <Route path="/suivi-pp" element={<Protected path="/suivi-pp"><SuiviPP /></Protected>} />
            <Route path="/suivi-pp/:id" element={<Protected path="/suivi-pp"><PPDetail /></Protected>} />
            <Route path="/tableau-pp" element={<Protected path="/tableau-pp"><TableauPP /></Protected>} />
            <Route path="/recommandations" element={<Protected path="/recommandations"><Recommandations /></Protected>} />
            <Route path="/domaines-pp" element={<Protected path="/domaines-pp"><DomainesPP /></Protected>} />
            <Route path="/objectifs-types-pp" element={<Protected path="/objectifs-types-pp"><ObjectifsTypesPP /></Protected>} />
            <Route path="/vie-sociale" element={<Protected path="/vie-sociale"><VieSociale /></Protected>} />
            <Route path="/messagerie" element={<Protected path="/messagerie"><Messagerie /></Protected>} />
            <Route path="/cuisine-agenda" element={<Protected path="/cuisine-agenda"><CuisineAgenda /></Protected>} />
            <Route path="/cuisine-planning" element={<Protected path="/cuisine-planning"><CuisinePlanning /></Protected>} />
            <Route path="/menu-semaine" element={<Protected path="/menu-semaine"><MenuSemaine /></Protected>} />
            <Route path="/cuisine-inventaire" element={<Protected path="/cuisine-inventaire"><CuisineInventaire /></Protected>} />
            <Route path="/petit-dejeuner" element={<Protected path="/petit-dejeuner"><PetitDejeuner /></Protected>} />
            <Route path="/menu-plats" element={<Protected path="/menu-plats"><MenuPlats /></Protected>} />
            <Route path="/repas-preparation" element={<Protected path="/repas-preparation"><RepasPreparation /></Protected>} />
            <Route path="/habitants" element={<Protected path="/habitants"><Habitants /></Protected>} />
            <Route path="/stats-repas" element={<Protected path="/stats-repas"><StatsRepas /></Protected>} />
            <Route path="/guide" element={<Protected><Guide /></Protected>} />
            <Route path="/espace-partenaires" element={<Protected path="/espace-partenaires" bare><EspacePartenaires /></Protected>} />
            <Route path="/utilisateurs" element={<Protected path="/utilisateurs"><Utilisateurs /></Protected>} />
            <Route path="/categories-activites" element={<Protected path="/categories-activites"><CategoriesActivites /></Protected>} />
            <Route path="/historique" element={<Protected path="/historique"><Audit /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <ScreenGuard />
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
